import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '../../lib/useSession';
import { getMultiFactorResolver, signInWithEmailAndPassword, type MultiFactorError } from 'firebase/auth';
import { getFirebaseAuth } from '../../lib/firebase';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Banner } from '../../components/Banner';
import { AuthShell } from './AuthShell';
import { authErrorMessage, setPendingResolver } from './mfa';

export function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { status } = useSession();
  const notice = (useLocation().state as { notice?: string } | null)?.notice;

  // Con sesión lista (recién iniciada o ya abierta), `/` decide el área: consola, terreno u operador.
  useEffect(() => {
    if (status === 'signed-in') navigate('/', { replace: true });
  }, [status, navigate]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const auth = getFirebaseAuth();
      await signInWithEmailAndPassword(auth, email.trim(), password);
      // La navegación ocurre en el efecto de arriba cuando /v1/me confirma la sesión.
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (code === 'auth/multi-factor-auth-required') {
        setPendingResolver(getMultiFactorResolver(getFirebaseAuth(), err as MultiFactorError));
        navigate('/login/verificar', { replace: true });
        return;
      }
      setError(err instanceof Error && !code ? err.message : authErrorMessage(code));
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell title="Iniciar sesión" subtitle="Consola de partners y app del vendedor.">
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {notice && !error && <Banner tone="success">{notice}</Banner>}
        {error && <Banner tone="error">{error}</Banner>}
        <Input label="Email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <Input
          label="Contraseña"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Button type="submit" block size="lg" loading={loading} disabled={!email || !password}>
          Entrar
        </Button>
        <p className="text-center text-sm">
          <Link to="/login/recuperar">¿Olvidaste tu contraseña?</Link>
        </p>
      </form>
    </AuthShell>
  );
}
